// src/components/XPDisplay.tsx
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { UserProgress } from '../types/xp';
import { LevelIcon } from './LevelIcon';
import { useAuthState } from '../hooks/useAuthState';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'XPDisplay', level: 'info' });

interface XPDisplayProps {
  onClick: () => void;
  collapsedMode?: boolean;
}

export const XPDisplay: React.FC<XPDisplayProps> = ({ onClick, collapsedMode = false }) => {
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuthState();
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const retryDelayMs = 1000;

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        if (!isAuthenticated || !user) {
          logger.debug('No authenticated user, skipping XP fetch');
          setProgress(null);
          setLoading(false);
          return;
        }

        // Check network connectivity before making the request
        if (!navigator.onLine) {
          logger.warn('Device is offline, skipping XP fetch');
          setLoading(false);
          
          // Listen for when we come back online
          const handleOnline = () => {
            window.removeEventListener('online', handleOnline);
            logger.info('Device back online, retrying XP fetch');
            fetchProgress();
          };
          window.addEventListener('online', handleOnline);
          return;
        }

        // Add a small delay for new sessions to allow network to stabilize
        const isNewSession = retryCountRef.current === 0;
        if (isNewSession) {
          logger.debug('New XP fetch session, adding stabilization delay');
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Use RPC directly to avoid CORS issues with edge function
        const { data, error } = await supabase.rpc('get_progress', {
          p_user_id: user.id
        });

        if (error) {
          logger.error('Error fetching XP progress:', {
            error: error.message || error.toString(),
            userId: user.id
          });
          
          // For any error, including network errors, retry if under max retries
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            logger.warn(`Network error fetching XP progress, retrying (attempt ${retryCountRef.current}/${maxRetries})`, {
              error: error.message || error.toString(),
              userId: user.id
            });
            
            // Exponential backoff
            const retryDelay = retryDelayMs * Math.pow(2, retryCountRef.current - 1);
            setTimeout(() => {
              fetchProgress();
            }, retryDelay);
            return;
          }
          
          // Set default progress for new users or on error
          setProgress({
            xp: {
              total_xp: 0,
              current_level: 'explorer',
              daily_xp: {},
              weekly_actions: {},
              monthly_actions: {}
            },
            achievements: [],
            streak: {
              current_streak: 0,
              longest_streak: 0,
              last_action_date: new Date().toISOString()
            },
            rewards: []
          });
        } else {
          setProgress(data);
          retryCountRef.current = 0; // Reset retry count on success
          logger.debug('XP progress fetched successfully', {
            data: data,
            totalXP: data?.xp?.total_xp,
            level: data?.xp?.current_level
          });
        }
      } catch (error) {
        logger.error('Unexpected error fetching XP progress:', {
          error: error instanceof Error ? error.message : String(error),
          userId: user?.id,
          retryCount: retryCountRef.current
        });
        setProgress(null);
      } finally {
        setLoading(false);
      }
    };

    // Only attempt to fetch progress if the user is authenticated
    if (isAuthenticated && user) {
      fetchProgress();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  if (loading) return <div className="p-2">Loading...</div>;
  
  // If no progress data and not authenticated, show placeholder
  if (!progress && !isAuthenticated) {
    return (
      <div 
        className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-200 transition-colors" 
        onClick={onClick}
      >
        <LevelIcon level="explorer" />
        {!collapsedMode && (
          <span className="text-sm font-medium">
            0 XP
          </span>
        )}
      </div>
    );
  }

  // Debug log
  logger.debug('Rendering XPDisplay', {
    progress: progress,
    totalXP: progress?.xp?.total_xp,
    collapsedMode: collapsedMode
  });

  return (
    <div 
      className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-200 transition-colors" 
      onClick={onClick}
    >
      <LevelIcon level={progress?.xp?.current_level || 'explorer'} />
      {!collapsedMode && (
        <span className="text-sm font-medium">
          {progress?.xp?.total_xp?.toLocaleString() || '0'} XP
        </span>
      )}
    </div>
  );
};
